import { isComplexElement, NodeType } from '@vedalib/editor'
import * as R from 'ramda'
import { useCallback } from 'react'

import { useOpenModal } from 'components/uiKit/Modal'
import { setProjectNavigation, updateBlock } from 'services/Store/Project/actions'
import { useProjectDispatch } from 'services/Store/Project/hooks'
import type { Block } from 'services/Store/Project/types'

import { getNode } from '../EditorContext/selectors/block'
import { createColNode, createShell } from '../EditorContext/struct/block'
import ElementPickerModal from './ElementPickerModal'

export const usePickElement = (block: Block, nodeId: string) => {
  const dispatch = useProjectDispatch()
  const onChange = useCallback(
    (block: Block) => dispatch(updateBlock({ id: block?.uuid, value: block })),
    [dispatch],
  )
  const elementModal = useOpenModal(ElementPickerModal)

  return useCallback(async () => {
    const element = await elementModal.open()
    const nodes = []
    let shellId = null
    const selectedNode = block && nodeId ? getNode(block, nodeId) : null
    if (selectedNode?.type === NodeType.shell) {
      shellId = nodeId
    } else if (selectedNode?.type === NodeType.column || selectedNode?.type === NodeType.absolute) {
      const newShell = createShell(element?.type)
      const updateColumn = {
        ...selectedNode,
        children: [...selectedNode.children, newShell.id],
      }
      nodes.push(newShell, updateColumn)
      shellId = newShell.id
    }
    if (!element || !shellId) {
      return
    }

    let newElement = element
    if (isComplexElement(element)) {
      const updatedItems = element.value.items.map((item) => {
        const column = createColNode()
        nodes.push(column)
        return R.assoc('value', column.id, item)
      })

      newElement = R.assocPath(['value', 'items'], updatedItems, element)
    }

    const path = ['schema', 'nodes', shellId, 'elementId']
    const newBlock = R.compose(
      R.assocPath(path, element.id),
      //@ts-expect-error compose type
      R.assocPath(['elements', element.id], newElement),
      ...nodes.map((node) => R.assocPath(['schema', 'nodes', node.id], node)),
    )(block)
    onChange(newBlock as Block)
    dispatch(setProjectNavigation({ nodeIds: [shellId] }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block, nodeId, dispatch, onChange])
}
